import React from "react";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  MessageCircle,
  UserPlus,
} from "lucide-react";
import { DetailContainer, BackButton } from "./UI";
import { getCategoryById } from "../services/CategoryModel";
import AttendeeProfile from "./AttendeeProfile";
import InviteUsersScreen from "./InviteUser";
import { useEvent } from "../hooks/useEvent";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { useUser } from "../context/user";

import styles from "./Events.module.css";

/**
 * EventDetailScreen component renders detailed information about an event,
 * lists its attendees, and provides actions to join or invite compatible users.
 */
export default function EventDetailScreen({
  event,
  onBack,
  onJoin,
  isJoined,
  onOpenMap,
  userInterests = ["tech", "coffee", "music"],
  onSelectEvent,
}) {
  const { userData } = useUser();
  const {
    showAttendees,
    setShowAttendees,
    showInviteScreen,
    setShowInviteScreen,
    invitedUsers,
    toastMessage,
    realAttendees,
    loadingAttendees,
    liveAttendeeCount,
    selectedAttendeeProfile,
    setSelectedAttendeeProfile,
    handleSendInvitation,
  } = useEvent(event, userData, userInterests, isJoined);

  const eventCategoryObj = getCategoryById(event.category);

  // Helper function to format attendee count message
  const renderAttendeeText = () => {
    const count = liveAttendeeCount;
    if (count === 0) return "No attendees yet";
    if (isJoined) {
      if (count === 1) return "You are attending";
      if (count === 2) return "You and 1 other person attending";
      return `You and ${count - 1} others attending`;
    } else {
      return `${count} ${
        count === 1 ? "person attending" : "people attending"
      }`;
    }
  };

  // Render: Selected attendee profile view
  if (selectedAttendeeProfile) {
    const profileId = String(
      selectedAttendeeProfile.id || selectedAttendeeProfile.uid
    );
    const isInvited = invitedUsers.includes(profileId);

    return (
      <AttendeeProfile
        user={selectedAttendeeProfile}
        event={event}
        isInvited={isInvited}
        onBack={() => setSelectedAttendeeProfile(null)}
        onInvite={(usr, e) => handleSendInvitation(usr, e)}
        onSelectEvent={(ev) => {
          setSelectedAttendeeProfile(null);
          if (onSelectEvent) {
            onSelectEvent(ev);
          }
        }}
      />
    );
  }

  // Render: Full screen scrollable invite screen
  if (showInviteScreen) {
    return (
      <InviteUsersScreen
        event={event}
        userInterests={userInterests}
        invitedUsers={invitedUsers}
        onBack={() => setShowInviteScreen(false)}
        onInviteUser={(user, e) => handleSendInvitation(user, e)}
      />
    );
  }

  return (
    <DetailContainer>
      {toastMessage && (
        <div className={styles.toastNotification}>
          <MessageCircle size={16} />
          <span>{toastMessage}</span>
        </div>
      )}

      <div className={styles.topBar}>
        <BackButton onClick={onBack}>
          <ArrowLeft size={20} />
        </BackButton>
        {event.category && (
          <span className={styles.categoryBadge}>
            {eventCategoryObj.icon} {eventCategoryObj.label}
          </span>
        )}
      </div>

      <div className={styles.heroBanner}>
        {event.image ? (
          <img
            src={event.image}
            alt={event.title}
            className={styles.heroImage}
          />
        ) : (
          <div
            className={`${styles.heroFallback} ${event.color || "bg-pink-100"}`}
          >
            <span className={styles.heroInitials}>
              {event.title.substring(0, 2).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.headerSection}>
          <h1 className={styles.eventTitle}>{event.title}</h1>
          <p className={styles.eventHost}>
            Hosted by:{" "}
            <span className={styles.hostName}>
              {event.host || "Dublin Community"}
            </span>
          </p>
          <p className={styles.eventDateText}>
            <Calendar size={14} className={styles.inlineIcon} /> {event.date}
          </p>
        </div>

        <div className={styles.attendeesAndActionsWrapper}>
          <div
            onClick={() => setShowAttendees(!showAttendees)}
            className={styles.attendeesTrigger}
          >
            <div className={styles.avatarGroup}>
              {realAttendees.slice(0, 3).map((user) => (
                <img
                  key={user.id}
                  src={getAvatarUrl(user)}
                  alt={user.name || "User"}
                  className={styles.miniAvatar}
                />
              ))}
              {liveAttendeeCount > 3 && (
                <div className={styles.moreAvatarCount}>
                  <span>+{liveAttendeeCount - 3}</span>
                </div>
              )}
            </div>
            <span className={styles.attendeesLabel}>
              {showAttendees ? "Hide attendees" : renderAttendeeText()}
            </span>
          </div>

          <div className={styles.actionsButtonRow}>
            <button
              onClick={() => setShowInviteScreen(true)}
              className={styles.inviteToggleBtn}
              title="Explore users to invite"
            >
              <UserPlus size={14} /> Invite
            </button>

            <button
              onClick={onJoin}
              className={`${styles.compactJoinBtn} ${
                isJoined ? styles.joinedActive : ""
              }`}
            >
              {isJoined ? "✓ Joined" : "Join Event"}
            </button>
          </div>
        </div>

        {showAttendees && (
          <div className={styles.expandedAttendeesList}>
            <div className={styles.matchListHeader}>
              <h4 className={styles.matchListTitle}>Event Attendees</h4>
              <span className={styles.matchListSubtitle}>
                Tap to view full profile
              </span>
            </div>

            {loadingAttendees ? (
              <p className={styles.statusMessage}>Loading attendees...</p>
            ) : realAttendees.length === 0 ? (
              <p className={styles.statusMessage}>No attendees yet.</p>
            ) : (
              realAttendees.map((user) => (
                <div
                  key={user.id}
                  onClick={() => setSelectedAttendeeProfile(user)}
                  className={styles.attendeeCard}
                >
                  <img
                    src={getAvatarUrl(user)}
                    alt={user.name}
                    className={styles.attendeeCardAvatar}
                  />
                  <div className={styles.attendeeInfo}>
                    <span className={styles.attendeeName}>
                      {user.name || "User"}
                    </span>
                    <span className={styles.noSharedTag}>Attendee</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className={styles.descriptionSection}>
          <h3 className={styles.descriptionTitle}>About this event</h3>
          <p className={styles.descriptionText}>
            {event.description ||
              event.desc ||
              "Join us for an amazing meetup experience, connect with like-speed people, and enjoy sharing great moments together in Dublin."}
          </p>
        </div>

        <div className={styles.detailsCard}>
          <div
            onClick={() => onOpenMap?.(event)}
            className={styles.clickableAddressRow}
            title="Click to view on map"
          >
            <div className={styles.detailIconWrapperHover}>
              <MapPin size={16} />
            </div>
            <span className={styles.addressText}>
              {event.location || "Dublin City Center"}
            </span>
            <span className={styles.mapActionHint}>View on Map →</span>
          </div>
        </div>
      </div>
    </DetailContainer>
  );
}
